import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/core/services/auth.service';
import { CommandeService } from 'src/app/core/services/commande.service';
import { ToastService } from 'src/app/core/services/toast.service';

@Component({
  selector: 'app-livreur-commandes',
  templateUrl: './livreur-commandes.component.html',
  styleUrls: ['./livreur-commandes.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class LivreurCommandesComponent implements OnInit {

  commandes: any[] = [];
  commandesFiltrees: any[] = [];
  indicateurChargement = true;
  termeRecherche = '';
  statutSelectionne = 'all';
  idLivreur: number | null = null;
  messageErreur = '';
  nomLivreur = '';
  moyenneNotes: number | null = null;
  nombreAvis = 0;
  avisRecents: { numeroCommande: string; note: number; commentaire?: string | null; date?: string | null }[] = [];

  readonly actionsStatuts: Record<string, { label: string; suivant: string[] }> = {
    'EN_ATTENTE':    { label: 'Commande enregistrée', suivant: [] },
    'EN_PREPARATION':{ label: 'En route vers le retrait', suivant: ['EN_LIVRAISON'] },
    'AFFECTE':       { label: 'Livreur assigné', suivant: ['EN_LIVRAISON'] },
    'EN_LIVRAISON':  { label: 'En livraison',   suivant: ['LIVRE'] },
    'LIVRE':         { label: 'Livré',          suivant: [] },
    'ANNULE':        { label: 'Annulé',         suivant: [] },
  };

  constructor(
    private commandeService: CommandeService,
    private authService: AuthService,
    private toast: ToastService,
    private router: Router
  ) {}

  ngOnInit(): void {
    const utilisateur = this.authService.obtenirUtilisateurCourant();
    this.idLivreur = utilisateur?.userid ?? null;
    this.nomLivreur = utilisateur ? `${utilisateur.prenom || ''} ${utilisateur.nom || ''}`.trim() : '';
    if (this.idLivreur) {
      this.chargerEvaluationsLivreur();
      this.chargerCommandes();
    } else {
      this.messageErreur = 'Impossible de récupérer votre identifiant. Veuillez vous reconnecter.';
      this.indicateurChargement = false;
    }
  }

  chargerEvaluationsLivreur(): void {
    this.commandeService.obtenirMesEvaluationsLivreur().subscribe({
      next: (res: any) => {
        this.moyenneNotes = typeof res?.moyenneNote === 'number' ? res.moyenneNote : null;
        this.nombreAvis = res?.nombreEvaluations ?? 0;
        const ev = res?.evaluations;
        this.avisRecents = Array.isArray(ev) ? ev.slice(0, 8) : [];
      },
      error: () => {
        this.moyenneNotes = null;
        this.nombreAvis = 0;
        this.avisRecents = [];
      }
    });
  }

  chargerCommandes(): void {
    this.indicateurChargement = true;
    this.messageErreur = '';
    this.commandeService.obtenirCommandesDuLivreur(this.idLivreur!).subscribe({
      next: (donnees: any) => {
        const liste = Array.isArray(donnees) ? donnees : (donnees?.content ?? []);
        this.commandes = liste;
        this.appliquerFiltres();
        this.indicateurChargement = false;
      },
      error: () => {
        this.messageErreur = 'Erreur lors du chargement de vos commandes. Vérifiez votre connexion.';
        this.indicateurChargement = false;
      }
    });
  }

  appliquerFiltres(): void {
    let resultat = [...this.commandes];
    if (this.termeRecherche) {
      const terme = this.termeRecherche.toLowerCase();
      resultat = resultat.filter(cmd =>
        (cmd.numeroCommande?.toLowerCase() || '').includes(terme) ||
        (cmd.numeroSuivi?.toLowerCase() || '').includes(terme) ||
        (cmd.clientNom?.toLowerCase() || '').includes(terme) ||
        (cmd.statut?.toLowerCase() || '').includes(terme)
      );
    }
    if (this.statutSelectionne !== 'all') {
      resultat = resultat.filter(cmd => cmd.statut === this.statutSelectionne);
    }
    this.commandesFiltrees = resultat;
  }

  changerStatut(commande: any, nouveauStatut: string): void {
    this.commandeService.mettreAJourStatutCommande(commande.id, nouveauStatut).subscribe({
      next: () => {
        this.toast.succes(`Statut mis à jour : ${this.obtenirTexteStatut(nouveauStatut)}`, 'Succès');
        this.chargerCommandes();
      },
      error: () => this.toast.erreur('Erreur lors de la mise à jour du statut', 'Erreur')
    });
  }

  obtenirStatutsActionsDisponibles(statut: string): string[] {
    return this.actionsStatuts[statut]?.suivant ?? [];
  }

  obtenirClasseBadgeStatut(statut: string): string {
    const map: Record<string, string> = {
      'EN_ATTENTE':    'status-pending',
      'EN_PREPARATION':'status-preparation',
      'AFFECTE':       'status-assigned',
      'EN_LIVRAISON':  'status-delivery',
      'LIVRE':         'status-delivered',
      'ANNULE':        'status-cancelled',
    };
    return map[statut] || 'status-pending';
  }

  obtenirTexteStatut(statut: string): string {
    return this.actionsStatuts[statut]?.label ?? statut;
  }

  compterParStatut(statut: string): number {
    return this.commandes.filter(c => c.statut === statut).length;
  }

  voirDetails(commande: any): void {
    this.router.navigate(['/commande', commande.id]);
  }
}
